/**
 * OAuth Error Detection and Handling Utilities
 *
 * Detects when accounting API calls fail due to invalid/expired OAuth credentials
 * and provides utilities to handle re-authorization
 */

export interface OAuthError {
  isOAuthError: boolean;
  accountingSystem?: string;
  errorMessage?: string;
  requiresReauth: boolean;
}

/**
 * Check if an API error response indicates an OAuth authorization problem
 */
export function isOAuthError(error: any): OAuthError {
  const errorMessage =
    typeof error === "string" ? error : error?.message || error?.error || "";

  // Check for common OAuth error patterns
  const oauthErrorPatterns = [
    "invalid_grant",
    "Invalid refresh token",
    "refresh token",
    "Failed to refresh token",
    "Invalid or expired credentials",
    "Failed to initialize Fortnox service",
    "Failed to initialize",
    "credentials",
    "Authorization",
    "unauthorized",
    "401",
  ];

  const isOAuth = oauthErrorPatterns.some((pattern) =>
    errorMessage.toLowerCase().includes(pattern.toLowerCase()),
  );

  if (!isOAuth) {
    return {
      isOAuthError: false,
      requiresReauth: false,
    };
  }

  // Try to detect which accounting system
  let accountingSystem: string | undefined;
  if (errorMessage.toLowerCase().includes("fortnox")) {
    accountingSystem = "fortnox";
  } else if (errorMessage.toLowerCase().includes("economic")) {
    accountingSystem = "economic";
  }

  return {
    isOAuthError: true,
    accountingSystem,
    errorMessage,
    requiresReauth: true,
  };
}

/**
 * Standard OAuth error response for API routes
 */
export function createOAuthErrorResponse(
  accountingSystem: string,
  message?: string,
) {
  return {
    success: false,
    error:
      message ||
      "Invalid or expired credentials. Please reconfigure the accounting system.",
    errorCode: "OAUTH_ERROR",
    requiresReauth: true,
    accountingSystem,
  };
}

/**
 * Extract accounting system from venue data
 */
export async function getAccountingSystemForVenue(
  venueId: string | number,
): Promise<string | null> {
  try {
    const response = await fetch(
      `/api/superadmin/venue/${venueId}/accounting-system`,
    );
    if (!response.ok) return null;

    const data = await response.json();
    return data.accountingSystem || null;
  } catch (error) {
    console.error("Failed to fetch accounting system:", error);
    return null;
  }
}
